import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCartStore } from "@/components/cart/store";
import { calculateCouponDiscount } from "@/lib/coupon-discount";
import { useLocaleNavigation } from "@/hooks/use-locale-navigation";
import {
  qrConfigurationQueryOptions,
  qrTablePaymentModeQueryOptions,
} from "@/lib/qr";
import { createQrOrder } from "@/lib/qr-orders";
import { productsQueryOptions } from "@/lib/products";
import { useMenuQuery } from "@/hooks/use-menu-query";
import { formatNumberToCurrency } from "@/lib/utils";
import { getMenuPicksExtrasTotal } from "@/lib/menu-picks";
import { OrderTypes } from "@/lib/supabase/enums";
import { useQrSessionStore } from "@/stores/qr-session";

/**
 * The QR "checkout": a review of the round and one button.
 *
 * There is deliberately no form here. The guest is sitting at a table, so there
 * is no name, no address, no phone and no delivery slot to collect — the only
 * thing the kitchen needs is what they want and where to bring it, and the
 * table already answered the second half.
 */
export const Route = createFileRoute("/$locale/qr/cart")({
  loader: ({ context }) => {
    context.queryClient.ensureQueryData(productsQueryOptions());
    return context.queryClient.ensureQueryData(qrConfigurationQueryOptions());
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation("translation");
  const { locale } = Route.useParams();
  const { navigateWithLocale } = useLocaleNavigation();

  const configuration = useSuspenseQuery(qrConfigurationQueryOptions());
  const { data: productsByCategory } = useMenuQuery();

  const session = useQrSessionStore((state) => state.session);
  const cart = useCartStore((state) => state.cart);
  const clearCart = useCartStore((state) => state.clearCart);
  const setCoupon = useCartStore((state) => state.setCoupon);

  // Which table the guest is at only exists in this browser's session storage,
  // so the route loader cannot prefetch this the way it does the configuration.
  const { data: tablePaymentMode } = useQuery(
    qrTablePaymentModeQueryOptions(session?.tableId)
  );

  const [comment, setComment] = useState("");
  const [error, setError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // A reload straight onto this URL has no table bound to it. Send the guest
  // back to scan rather than guessing at a table.
  useEffect(() => {
    if (!session) navigateWithLocale("/qr", { replace: true });
  }, [session, navigateWithLocale]);

  const items = cart.items.filter((item) => item.enabled !== false);

  const products = productsByCategory?.flatMap((category) => category.products);

  // Display only. Every price that ends up on the bill is resolved server-side
  // in createQrOrder from the same take-away price list.
  const lines = items.map((item) => {
    const product = products?.find((entry) => entry.id === item.productId);
    const optionsPrice =
      product?.productOptionSubgroups?.reduce((optionsSum, subgroup) => {
        const selected = item.selectedOptions?.[subgroup.id];
        if (!selected) return optionsSum;

        return (
          optionsSum +
          selected
            .split(",")
            .map((id) => id.trim())
            .reduce(
              (price, optionId) =>
                price +
                (subgroup.productOptions?.find(
                  (option) => option.id === optionId
                )?.price ?? 0),
              0
            )
        );
      }, 0) ?? 0;
    // A menu's supplements and the options on its picks. A QR round is priced
    // from the take-away list, which the pickup prices are.
    const menuExtrasPrice = getMenuPicksExtrasTotal(
      product?.menuCourses,
      item,
      OrderTypes.pickup
    );

    return {
      productId: item.productId,
      productCategoryId: product?.productCategoryId,
      total: (item.price + optionsPrice + menuExtrasPrice) * item.quantity,
    };
  });

  const subtotal = lines.reduce((sum, line) => sum + line.total, 0);

  // Whatever coupon is still on the basket has already been weighed against the
  // in_store context, so it is one that holds at a table. The register settles
  // it from the allocations createQrOrder writes onto the lines; this is only
  // the guest's preview of the same number.
  const couponDiscount = calculateCouponDiscount({
    coupon: cart.coupon?.coupon,
    lines,
  });

  const total = subtotal - couponDiscount;

  const handleSubmit = async () => {
    if (!session || items.length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    setError(undefined);

    try {
      const result = await createQrOrder({
        data: {
          tableId: session.tableId,
          locale,
          comment: comment.trim() || undefined,
          couponCode: cart.coupon?.code,
          items: items.map((item) => ({
            id: item.id,
            productId: item.productId,
            quantity: item.quantity,
            comment: item.comment,
            selectedOptions: item.selectedOptions,
            menuPicks: item.menuPicks,
            // Legacy shape, only on lines saved before menu picks existed.
            menuSelections: item.menuSelections,
            menuOptionSelections: item.menuOptionSelections,
          })),
        },
      });

      if (!result.success) {
        // A coupon that no longer holds comes off the basket, so the total the
        // guest is looking at is the one the next attempt will charge.
        if (result.code === "coupon_error") setCoupon(undefined);

        setError(
          result.code === "order_closed"
            ? t("Qr.order_closed_description")
            : result.code === "coupon_error"
              ? t("Qr.coupon_error")
              : result.code === "product_no_longer_available" &&
                  result.productNames?.length
                ? t("Qr.products_unavailable_description", {
                    productNames: result.productNames.join(", "),
                  })
                : t("Qr.general_error")
        );
        return;
      }

      // Pay-first mode hands off to Mollie; the round is only fired once the
      // webhook confirms it, so the cart is kept until then.
      if (result.paymentUrl) {
        window.location.href = result.paymentUrl;
        return;
      }

      clearCart();
      navigateWithLocale(`/qr/round/${result.qrRoundId}`, { replace: true });
    } catch {
      setError(t("Qr.general_error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!session) return null;

  // The section the table sits in decides this; the venue-wide setting is what
  // a section without one of its own falls back to, and it is also the honest
  // answer while the table's own mode is still loading.
  const payNow =
    (tablePaymentMode ?? configuration.data.paymentMode) === "online";

  return (
    <div className="mx-auto flex w-full max-w-lg flex-1 flex-col gap-5 p-4">
      <div className="rounded-xl border bg-white p-4 shadow">
        <p className="text-sm text-gray-500">{t("Qr.ordering_for")}</p>
        <p className="text-xl font-bold">
          {t("Qr.table_label", { reference: session.tableReference })}
          {session.sectionName ? ` - ${session.sectionName}` : ""}
        </p>
      </div>

      <div className="divide-y rounded-xl border bg-white shadow">
        {items.map((item) => (
          <div key={item.id} className="flex gap-3 p-4">
            <span className="font-bold">{item.quantity}x</span>
            <div className="flex-1">
              <p className="font-medium">{item.name}</p>
              {item.comment && (
                <p className="text-sm text-gray-500">{item.comment}</p>
              )}
            </div>
            <span className="font-medium">
              {formatNumberToCurrency({
                number: new Decimal(item.price).mul(item.quantity).toNumber(),
              })}
            </span>
          </div>
        ))}

        {items.length === 0 && (
          <p className="p-6 text-center text-gray-500">{t("Qr.cart_empty")}</p>
        )}
      </div>

      {configuration.data.allowComment && (
        <div className="space-y-2">
          <label htmlFor="qr-comment" className="font-medium">
            {t("Qr.comment_label")}
          </label>
          <textarea
            id="qr-comment"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            maxLength={200}
            rows={3}
            placeholder={t("Qr.comment_placeholder")}
            className="w-full rounded-lg border p-3 outline-none focus:border-black"
          />
        </div>
      )}

      <div className="flex flex-col gap-2">
        {couponDiscount > 0 && (
          <>
            <div className="flex items-center justify-between">
              <span>{t("Cart.subtotal")}</span>
              <span>{formatNumberToCurrency({ number: subtotal })}</span>
            </div>
            <div className="flex items-center justify-between text-green-600">
              <span>
                {t("Checkout.OrderSummary.coupon_discount")}
                {cart.coupon?.code ? ` (${cart.coupon.code})` : ""}
              </span>
              <span className="font-semibold">
                -{formatNumberToCurrency({ number: couponDiscount })}
              </span>
            </div>
          </>
        )}
        <div className="flex items-center justify-between text-lg font-bold">
          <span>{t("Qr.total")}</span>
          <span>{formatNumberToCurrency({ number: total })}</span>
        </div>
      </div>

      {error && (
        <p className="text-center text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      <p className="text-center text-sm text-gray-500">
        {payNow ? t("Qr.pay_now_hint") : t("Qr.pay_at_table_hint")}
      </p>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={items.length === 0 || isSubmitting}
        className="min-h-12 w-full cursor-pointer rounded-full bg-black/90 p-2 text-lg font-bold text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isSubmitting
          ? t("Qr.placing_order")
          : payNow
            ? t("Qr.pay_and_order")
            : t("Qr.place_order")}
      </button>
    </div>
  );
}
